import { isFunction } from "../utils/utils"
import { ReactOmniaModule } from "./module"

export function useDisableShell(shell: Pick<ReactOmniaModule, "disableShell" | "suspendRefresh">, disable: boolean) {

    React.useEffect(() => {

        const d1 = shell.disableShell(disable)
        const d2 = disable ? shell.suspendRefresh() : undefined

        return () => {
            d1 && d1()
            d2 && d2()
        }

    }, [disable])

}

export function useSuspendRefresh(module: ReactOmniaModule, suspend: boolean = true) {

    React.useEffect(() => suspend ? module.suspendRefresh() : undefined, [suspend])

}

export function useDispose(dispose: Function) {

    const ref = React.useRef<Function>()

    ref.current = dispose

    return React.useEffect(() => {

        return () => {

            const dispose = ref.current
            ref.current = undefined

            if (isFunction(dispose)) {
                dispose()
            }

        }

    }, [])

}

export function useAsyncValue<T>(provider: () => PromiseLike<T>, dependencies: React.DependencyList) {

    const [value, setValue] = React.useState<T>()

    React.useEffect(() => {

        let pending = true

        const promise = provider()

        if (promise) {

            promise.then(r => {

                if (pending) {
                    setValue(r)
                    pending = false
                }

            })

        }
        else {

            setValue(undefined)

        }

        return () => { pending = false }

    }, dependencies)

    return value

}

/**
 * This can be used in popup-like components, to get notifications when the user clicks
 * outside the component
 *
 * @param open Indicate if the popup is currently open
 * @param close Called when the popup should close
 */
export function useOutsideClick(open: boolean, close: () => void) {

    const rootRef = React.useRef<HTMLDivElement>()

    React.useEffect(() => {

        if (open) {

            document.addEventListener("click", handleOutsideClick, true)

            return () => document.removeEventListener("click", handleOutsideClick, true)

        }

        function handleOutsideClick(event: PointerEvent) {

            const target = event.target as Element

            if (rootRef.current.contains(target)) {
                return
            }

            close()

        }

    }, [open])

    return rootRef

}

export function useForceUpdate() {
    const [, forceUpdate] = React.useReducer(x => x + 1, 0)
    return forceUpdate
}

export function useDebouncedValue<T>(value: T, delay: number) {

    const [debouncedValue, setDebouncedValue] = React.useState(value)

    React.useEffect(() => {

        if (!delay) {
            setDebouncedValue(value)
            return
        }

        const timeout = window.setTimeout(() => setDebouncedValue(value), delay)

        return () => window.clearTimeout(timeout)

    }, [value, delay])

    return debouncedValue

}

export function useDebouncedValueWithSetter<T>(value: T, delay: number, setter: (value: T) => void) {
    value = useDebouncedValue(value, delay)
    React.useEffect(() => setter(value), [value])
}

/**
 * Track the document's active element
 */
export function useActiveElement(track: boolean = true) {

    const [active, setActive] = React.useState<Element | null>(null)

    React.useEffect(() => {

        const handleFocusIn = (e: FocusEvent) => {
            setActive(document.activeElement);
        }

        document.addEventListener('focusin', handleFocusIn)

        return () => {
            document.removeEventListener('focusin', handleFocusIn)
        }

    }, [track])

    return active

}

export function useSizeObserver<T extends HTMLElement>() {

    type Size = [width: number, height: number]

    const [size, setSize] = React.useState<Size>([0, 0])

    const ref = React.useRef<T>(null)

    React.useEffect(() => {
        if (ref.current) {
            const obs = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                const target = entries[0]?.target
                if (target) {
                    setSize([target.clientWidth, target.clientHeight])
                }
            })

            obs.observe(ref.current)

            return () => obs.disconnect()
        }
    }, [ref])

    return { ref, width: size[0], height: size[1] }

}

